import { AspectRatio, Box, Button, HStack, Text } from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import {
  POSTS_TYPE_VALUE_ARTICLE,
  POSTS_TYPE_VALUE_ASSOCIATION,
  POSTS_TYPE_VALUE_EVENT,
  POSTS_TYPE_VALUE_PAGE,
} from "@raiden/library/constants/posts";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage, useIntl } from "react-intl";
import { DataHandler } from "../../../../components/DataHandler";
import { Href } from "../../../../components/Href";
import { Icon } from "../../../../components/Icon";
import { getPostPath } from "../../../../helpers/posts/getPostPath";

/**
 * @typedef {Object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>>} swrResponsePosts
 * @property {import("@raiden/library/types/Post").PostType | undefined} type
 * @property {string} cardMaxWidth
 * @property {string} [caption]
 */
/**
 * @param {Props} props
 */
export function PostViewSuggestions({
  swrResponsePosts,
  type,
  cardMaxWidth,
  caption = "",
}) {
  const intl = useIntl();

  const translate = useTranslate();

  const isPage = type === POSTS_TYPE_VALUE_PAGE;
  const isArticle = type === POSTS_TYPE_VALUE_ARTICLE;
  const isAssociation = type === POSTS_TYPE_VALUE_ASSOCIATION;
  const isEvent = type === POSTS_TYPE_VALUE_EVENT;

  return (
    <DataHandler
      swrResponse={swrResponsePosts}
      renderNoResultSplash={false}
      keepDataOnRevalidation>
      {({ data: posts }) => (
        <>
          {posts &&
            posts?.map((post) => (
              <Href
                w={isAssociation ? "370px" : "full"}
                py=".2rem"
                px=".5rem"
                borderRadius="4px"
                maxW={{
                  base: "none",
                  md: cardMaxWidth,
                }}
                href={getPostPath({
                  postId: post.id,
                  postType: post.type,
                  postSlug: post.current_revision?.slug ?? null,
                  translate,
                })}
                _hover={{
                  bg: "brandPrimary.500",
                }}
                key={post.id}>
                <HStack
                  position={isAssociation ? "relative" : "initial"}
                  spacing={isAssociation ? "0" : ".5rem"}>
                  <Box
                    w="full"
                    maxW={isAssociation ? "auto" : "89px"}
                    h={isAssociation ? "150px" : "auto"}
                    overflow="hidden"
                    backgroundColor="gray.50">
                    <AspectRatio
                      ratio={387 / 217}
                      top={isAssociation ? "-50" : "0"}>
                      <PampaImage
                        image={post?.current_revision?.cover}
                        placeholderSrc={generateFrontPath({
                          id: "@front.internal-assets",
                          parameters: {
                            filePath: "placeholder.svg",
                          },
                          includeBasePath: true,
                        })}
                        mode="crop"
                        format={isAssociation ? "association" : "small"}
                        w="full"
                        alt={caption}
                      />
                    </AspectRatio>
                  </Box>

                  <Box
                    w="full"
                    h={isAssociation ? "50%" : "auto"}
                    bg={isAssociation ? "gray.50" : "none"}
                    bottom={isAssociation ? "0" : "initial"}
                    position={isAssociation ? "absolute" : "initial"}
                    overflow="hidden">
                    <Text
                      w="full"
                      pl=".5rem"
                      pt=".5rem"
                      fontSize={isAssociation ? "1.25rem" : "1.125rem"}
                      fontWeight={500}
                      lineHeight="1.75rem"
                      color={isAssociation ? "brandPrimary.700" : "white"}
                      noOfLines={1}>
                      {post.current_revision &&
                        translate(post.current_revision.title)}
                    </Text>

                    {isAssociation ? (
                      <HStack justify="flex-end" w="full" mt=".5rem" pr=".5rem">
                        <Button
                          rightIcon={
                            <Icon
                              icon="ms_chevron_right"
                              color="gray.400"
                              size="1.5rem"
                            />
                          }
                          variant="link">
                          <Text
                            variant="button-small"
                            _hover={{
                              textDecoration: "underline",
                            }}>
                            <FormattedMessage defaultMessage="En savoir plus" />
                          </Text>
                        </Button>
                      </HStack>
                    ) : (
                      <Text
                        pl=".5rem"
                        fontSize=".875rem"
                        fontWeight={500}
                        color="gray.400"
                        noOfLines={1}>
                        {isPage && post.current_revision?.summary && (
                          <>{translate(post.current_revision?.summary)}</>
                        )}

                        {isArticle && post.publication_date && (
                          <FormattedMessage
                            defaultMessage="Publié le {date}"
                            values={{
                              date: intl.formatDate(post.publication_date, {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              }),
                            }}
                          />
                        )}

                        {isEvent && (
                          <>
                            {post.current_revision?.event_start_at ? (
                              <FormattedMessage
                                defaultMessage="{start_date} {end_date}"
                                values={{
                                  start_date: intl.formatDate(
                                    post?.current_revision?.event_start_at,
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  ),
                                  end_date:
                                    post.current_revision.event_end_at &&
                                    `- ${intl.formatDate(
                                      post.current_revision.event_end_at,
                                      {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      },
                                    )}`,
                                }}
                              />
                            ) : (
                              <FormattedMessage defaultMessage="Aucune date renseignée" />
                            )}
                          </>
                        )}
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Href>
            ))}
        </>
      )}
    </DataHandler>
  );
}
